import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  NumberInput,
  Autocomplete,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconLanguage,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/page-header';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import {
  createLanguage,
  deleteLanguage,
  listLanguages,
  updateLanguage,
  type Language,
  type LanguageDraft,
} from '@/lib/languages';
import {
  listVoices,
  voicesFor,
  type SpeechVoice,
} from '@/lib/speech';
import classes from './settings-page.module.css';

/** What the form is for: adding a language, or editing the one it holds. */
type Subject = { mode: 'create' } | { mode: 'edit'; language: Language };

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/**
 * Client-side checks, mirroring what the server enforces.
 *
 * A convenience so a typo does not cost a round trip — not a control. This
 * bundle is public and modifiable, so the API validates the same fields again
 * and is the only thing that decides what gets stored.
 */
function validate(
  draft: LanguageDraft,
): Partial<Record<keyof LanguageDraft, string>> {
  const errors: Partial<Record<keyof LanguageDraft, string>> = {};

  if (!draft.name.trim()) errors.name = 'Required.';
  if (!draft.nativeName.trim()) errors.nativeName = 'Required.';

  const code = draft.code.trim();
  if (!code) {
    errors.code = 'Required.';
  } else if (!/^[a-zA-Z]{2,3}(-[a-zA-Z]{2,4})?$/.test(code)) {
    errors.code = 'A language code, such as it or zh-Hans.';
  }

  return errors;
}

interface LanguageFormProps {
  subject: Subject;
  /** Every voice the synthesiser offers, filtered here to this language. */
  voices: SpeechVoice[];
  /** True when the list could not be fetched, as opposed to being empty. */
  voicesUnavailable: boolean;
  onClose: () => void;
  onSaved: (language: Language, mode: Subject['mode']) => void;
}

/**
 * One form for both adding and editing.
 *
 * The two differ by their title, their submit label and whether an id is being
 * patched. Splitting them in two would duplicate three inputs and their
 * validation to express that.
 *
 * Mounted per subject with a `key`, so the fields start from the right values
 * and an abandoned edit cannot bleed into the next one — the alternative is an
 * effect that resets four pieces of state on every open.
 */
function LanguageForm({
  subject,
  voices,
  voicesUnavailable,
  onClose,
  onSaved,
}: LanguageFormProps) {
  const [draft, setDraft] = useState<LanguageDraft>(() =>
    subject.mode === 'edit'
      ? {
          name: subject.language.name,
          code: subject.language.code,
          nativeName: subject.language.nativeName,
          displayOrder: subject.language.displayOrder,
          ttsVoice: subject.language.ttsVoice,
          replayFallback: subject.language.replayFallback,
        }
      : {
          name: '',
          code: '',
          nativeName: '',
          displayOrder: null,
          ttsVoice: null,
          replayFallback: null,
        },
  );
  // Filtered on the code being edited, because the catalogue stores ISO 639-1
  // and a voice is named in BCP-47. That is not cosmetic: the provider rejects
  // a request whose language code does not match its voice, so an it-IT voice
  // offered on a Spanish row would produce a language that never plays.
  const offered = voicesFor(voices, draft.code);

  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof LanguageDraft, string>>
  >({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const trimmed: LanguageDraft = {
      name: draft.name.trim(),
      code: draft.code.trim(),
      nativeName: draft.nativeName.trim(),
      displayOrder: draft.displayOrder,
      // Blank arrives as null rather than as "". Clearing the box is how an
      // operator says this language has no voice, and that has to reach the
      // API as a null or it is not the default state.
      ttsVoice: draft.ttsVoice?.trim() ? draft.ttsVoice.trim() : null,
      replayFallback: draft.replayFallback?.trim()
        ? draft.replayFallback.trim()
        : null,
    };

    setIsSaving(true);
    setSubmitError(null);
    try {
      const saved =
        subject.mode === 'edit'
          ? await updateLanguage(subject.language.id, trimmed)
          : await createLanguage(trimmed);
      onSaved(saved, subject.mode);
      onClose();
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  // A conflict is about one field and belongs beside it, not in a banner at
  // the top of a three-field form.
  const conflict =
    submitError instanceof ApiError && submitError.is(ApiErrorCode.CONFLICT)
      ? submitError.message
      : null;

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        {submitError !== null && conflict === null && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Text size="sm">{describeError(submitError)}</Text>
          </Alert>
        )}

        <TextInput
          label="Language"
          description="The English name, as the team refers to it."
          placeholder="Italian"
          required
          data-autofocus
          value={draft.name}
          error={fieldErrors.name}
          onChange={(event) =>
            setDraft({ ...draft, name: event.currentTarget.value })
          }
        />

        <TextInput
          label="Code"
          description="ISO 639-1. Stored lowercase, so IT and it are one language."
          placeholder="it"
          required
          value={draft.code}
          error={fieldErrors.code ?? conflict ?? undefined}
          onChange={(event) =>
            setDraft({ ...draft, code: event.currentTarget.value })
          }
        />

        <TextInput
          label="Native name"
          description="The name in the language itself, in its own script."
          placeholder="Italiano"
          required
          dir="auto"
          value={draft.nativeName}
          error={fieldErrors.nativeName}
          onChange={(event) =>
            setDraft({ ...draft, nativeName: event.currentTarget.value })
          }
        />

        <Autocomplete
          label="Voice"
          description={
            offered.length > 0
              ? 'What this language sounds like, unless a scene overrides it. Leave blank and its lines have no audio at all.'
              : voicesUnavailable
                ? 'The voice list could not be loaded, so type a name. Leave blank for no audio.'
                : draft.code.trim()
                  ? 'No synthesiser voices for this language code. Leave blank for no audio.'
                  : 'Enter a code first, and the voices for it are offered here.'
          }
          placeholder="No voice — this language has no audio"
          data={offered.map((voice) => voice.name)}
          value={draft.ttsVoice ?? ''}
          onChange={(value) =>
            setDraft({ ...draft, ttsVoice: value === '' ? null : value })
          }
        />

        <Textarea
          label="Replay fallback"
          description="What Act III says when no level could be worked out from the conversation. Leave blank for the default. It has to be true of somebody who opened a café and said nothing, so it cannot claim they held a conversation — and it never says test, level, assessment or score."
          placeholder="The default sentence"
          autosize
          minRows={2}
          maxLength={240}
          value={draft.replayFallback ?? ''}
          error={fieldErrors.replayFallback}
          onChange={(event) =>
            setDraft({
              ...draft,
              replayFallback:
                event.currentTarget.value === ''
                  ? null
                  : event.currentTarget.value,
            })
          }
        />

        <NumberInput
          label="Order"
          description="Lower comes first. Leave it blank and the language sits at the end. The first three lead the app's opening question."
          placeholder="Unranked"
          min={0}
          max={9999}
          allowDecimal={false}
          value={draft.displayOrder ?? ''}
          error={fieldErrors.displayOrder}
          onChange={(value) =>
            setDraft({
              ...draft,
              displayOrder: value === '' ? null : Number(value),
            })
          }
        />

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving}>
            {subject.mode === 'edit' ? 'Save changes' : 'Add language'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

interface RemoveFormProps {
  language: Language;
  onClose: () => void;
  onRemoved: (language: Language) => void;
}

/**
 * Confirmation before a delete.
 *
 * The delete is a hard one — nothing references a language yet, so there is no
 * soft-delete column to set — which makes a mis-click unrecoverable except by
 * typing the row back in.
 */
function RemoveForm({ language, onClose, onRemoved }: RemoveFormProps) {
  const [error, setError] = useState<unknown>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function confirm() {
    setIsRemoving(true);
    try {
      await deleteLanguage(language.id);
      onRemoved(language);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      <Text size="sm">
        {language.name} leaves the catalogue. Nothing else on the platform
        points at a language yet, so this deletes the row outright — getting it
        back means adding it again.
      </Text>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="gray"
          onClick={onClose}
          disabled={isRemoving}
        >
          Cancel
        </Button>
        <Button color="red" loading={isRemoving} onClick={() => void confirm()}>
          Remove
        </Button>
      </Group>
    </Stack>
  );
}

export function LanguagesPage() {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [voices, setVoices] = useState<SpeechVoice[]>([]);
  const [voicesUnavailable, setVoicesUnavailable] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  // What each dialog is about, and whether it is open, held separately.
  // Clearing the subject on close would empty the dialog while it is still
  // fading out; leaving it set until the next open keeps a close looking like
  // a close.
  const [subject, setSubject] = useState<Subject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [removing, setRemoving] = useState<Language | null>(null);
  const [isRemoveOpen, setRemoveOpen] = useState(false);

  function openForm(next: Subject) {
    setSubject(next);
    setFormOpen(true);
  }

  function openRemove(language: Language) {
    setRemoving(language);
    setRemoveOpen(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const list = await listLanguages();
        if (cancelled) return;
        setLanguages(list);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }

      // Fetched separately and allowed to fail, the way the model list is on
      // the language model screen. A deployment with no synthesiser still has
      // a catalogue to edit, so the voice field falls back to free text rather
      // than the page falling over.
      try {
        const list = await listVoices();
        if (!cancelled) setVoices(list);
      } catch (caught) {
        if (cancelled) return;
        setVoices([]);
        // "No synthesiser configured" is not the same as "the call failed",
        // and only the second is worth telling somebody to type around.
        setVoicesUnavailable(
          !(
            caught instanceof ApiError &&
            caught.is(ApiErrorCode.SPEECH_NOT_CONFIGURED)
          ),
        );
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The order the API sorts by, held locally so an edit lands in place.
   *
   * It has to match `CATALOGUE_ORDER` in the API exactly: display order first
   * with unranked at the back, then name. Get this wrong and a saved language
   * appears in one place until the page is reloaded and it moves.
   */
  function sorted(list: Language[]): Language[] {
    return [...list].sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        if (a.displayOrder === null) return 1;
        if (b.displayOrder === null) return -1;
        return a.displayOrder - b.displayOrder;
      }
      return a.name.localeCompare(b.name);
    });
  }

  function onSaved(saved: Language, mode: Subject['mode']) {
    setLanguages((current) =>
      sorted(
        mode === 'edit'
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [...current, saved],
      ),
    );
    notifications.show({
      color: 'brand',
      message:
        mode === 'edit' ? `${saved.name} updated.` : `${saved.name} added.`,
    });
  }

  function onRemoved(removed: Language) {
    setLanguages((current) => current.filter((item) => item.id !== removed.id));
    notifications.show({ color: 'gray', message: `${removed.name} removed.` });
  }

  const header = (
    <PageHeader
      title="Languages"
      description="What Fluently can teach. Learners choose from this list."
      actions={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openForm({ mode: 'create' })}
          disabled={isLoading || loadError !== null}
        >
          Add language
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the languages"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {languages.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconLanguage size={28} stroke={1.4} />
            <Text fw={600}>No languages yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              The catalogue is empty. Add the first language and it becomes
              available to everything that reads this list.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={520}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>Order</Table.Th>
                  <Table.Th>Language</Table.Th>
                  <Table.Th w={110}>Code</Table.Th>
                  <Table.Th>Native name</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {languages.map((language) => (
                  <Table.Tr key={language.id}>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {language.displayOrder ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {language.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color="gray"
                        className={classes.code}
                      >
                        {language.code.toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {/* Endonyms carry their own direction — العربية runs the
                          other way — so the browser decides per value. */}
                      <Text size="sm" dir="auto" className={classes.native}>
                        {language.nativeName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Edit ${language.name}`}
                          onClick={() => openForm({ mode: 'edit', language })}
                        >
                          <IconPencil size={16} stroke={1.7} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${language.name}`}
                          onClick={() => openRemove(language)}
                        >
                          <IconTrash size={16} stroke={1.7} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Text size="xs" c="dimmed">
        Order decides more than this table. The first three languages lead the
        app&rsquo;s opening question and the rest sit behind &ldquo;Something
        else&rdquo;; blank means unranked, and unranked sits at the back.
        Whether a language is actually available to learners, and what content
        sits behind each one, are separate decisions — they arrive as fields
        here once they are made.
      </Text>

      <Modal
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        centered
        title={
          subject?.mode === 'edit'
            ? `Edit ${subject.language.name}`
            : 'Add a language'
        }
      >
        {subject && (
          <LanguageForm
            key={subject.mode === 'edit' ? subject.language.id : 'create'}
            subject={subject}
            voices={voices}
            voicesUnavailable={voicesUnavailable}
            onClose={() => setFormOpen(false)}
            onSaved={onSaved}
          />
        )}
      </Modal>

      <Modal
        opened={isRemoveOpen}
        onClose={() => setRemoveOpen(false)}
        centered
        title={`Remove ${removing?.name ?? 'language'}?`}
      >
        {removing && (
          <RemoveForm
            key={removing.id}
            language={removing}
            onClose={() => setRemoveOpen(false)}
            onRemoved={onRemoved}
          />
        )}
      </Modal>
    </Stack>
  );
}
